import { apiFetch } from './client';

export interface FileRecord {
  id: string;
  name: string;
  size: number;
  type: string;
  sender: { id: string; name: string };
  receiver: { id: string; name: string };
  direction: string;
  status: string;
  date: string;
  starred: boolean;
  deleted: boolean;
  checksum: string;
  expires: string;
  tags: string[];
}

export interface FileStats {
  all: number;
  sent: number;
  received: number;
  pending: number;
  starred: number;
  deleted: number;
}

export interface StorageUsage {
  usedBytes: number;
  quotaBytes: number;
}

export interface FileListResponse {
  files: FileRecord[];
  total: number;
}

export interface FileFilters {
  direction?: string;
  status?: string;
  search?: string;
  starred?: boolean;
  deleted?: boolean;
  page?: number;
  limit?: number;
}

export interface FileTransfer {
  id: string;
  fileId: string;
  fileName: string;
  senderId: string;
  senderName: string;
  receiverId: string;
  receiverName: string;
  direction: string;
  status: string;
  scheduledAt?: string;
  completedAt?: string;
  createdAt: string;
}

export interface TransferListResponse {
  transfers: FileTransfer[];
  total: number;
}

export interface TransferFilters {
  direction?: string;
  status?: string;
  page?: number;
  limit?: number;
}

export interface CreateTransferPayload {
  fileNames: string[];
  recipientIds: string[];
  notifyByEmail?: boolean;
  requireAck?: boolean;
  expiryDate?: string;
  scheduledDate?: string;
}

export interface ShareLink {
  id: string;
  fileId: string;
  url: string;
  expiresAt: string;
  createdAt: string;
}

export interface CreateSharePayload {
  fileId: string;
  expiresAt?: string;
}

const buildQuery = (params: Record<string, string | number | boolean | undefined>): string => {
  const query = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined) query.set(key, String(value));
  }
  const qs = query.toString();
  return qs ? `?${qs}` : '';
};

export const filesApi = {
  listFiles: (filters: FileFilters = {}): Promise<FileListResponse> => {
    const qs = buildQuery(filters as Record<string, string | number | boolean | undefined>);
    return apiFetch<FileListResponse>(`/files${qs}`);
  },

  getFileStats: (): Promise<FileStats> => {
    return apiFetch<FileStats>('/files/stats');
  },

  getStorageUsage: (): Promise<StorageUsage> => {
    return apiFetch<StorageUsage>('/files/storage');
  },

  getFile: (id: string): Promise<FileRecord> => {
    return apiFetch<FileRecord>(`/files/${id}`);
  },

  uploadFile: (file: File, folder?: string): Promise<FileRecord> => {
    const formData = new FormData();
    formData.append('file', file);
    if (folder) formData.append('folder', folder);
    return apiFetch<FileRecord>('/files', {
      method: 'POST',
      data: formData,
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },

  updateFile: (id: string, data: Partial<Pick<FileRecord, 'name' | 'tags'>>): Promise<FileRecord> => {
    return apiFetch<FileRecord>(`/files/${id}`, {
      method: 'PUT',
      data,
    });
  },

  deleteFile: (id: string): Promise<void> => {
    return apiFetch<void>(`/files/${id}`, { method: 'DELETE' });
  },

  toggleStar: (id: string): Promise<FileRecord> => {
    return apiFetch<FileRecord>(`/files/${id}/star`, { method: 'POST' });
  },

  bulkDelete: (ids: string[]): Promise<{ message: string; count: number }> => {
    return apiFetch<{ message: string; count: number }>('/files/bulk-delete', {
      method: 'POST',
      data: { ids },
    });
  },

  downloadFile: (id: string): Promise<Blob> => {
    return apiFetch<Blob>(`/files/${id}/download`, { responseType: 'blob' });
  },

  listTransfers: (filters: TransferFilters = {}): Promise<TransferListResponse> => {
    const qs = buildQuery(filters as Record<string, string | number | boolean | undefined>);
    return apiFetch<TransferListResponse>(`/files/transfers${qs}`);
  },

  forwardFile: (data: CreateTransferPayload): Promise<FileTransfer> => {
    return apiFetch<FileTransfer>('/files/forward', {
      method: 'POST',
      data,
    });
  },

  createShare: (data: CreateSharePayload): Promise<ShareLink> => {
    return apiFetch<ShareLink>('/files/shares', {
      method: 'POST',
      data,
    });
  },

  revokeShare: (id: string): Promise<void> => {
    return apiFetch<void>(`/files/shares/${id}`, { method: 'DELETE' });
  },
};
